import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, Input, signal, WritableSignal } from "@angular/core";
import { IconModule } from "@ogs/ngx-m3-components";
import { MatList, MatListModule } from "@angular/material/list";
import { NgTemplateOutlet, UpperCasePipe } from "@angular/common";
import { SIDEBAR_OFFSET_REF } from "@core/components/ui/sidebar/sidebar.component";
import { SidebarListItemComponent } from "@core/components/ui/sidebar/components/list-item/sidebar-list-item.component";
import { FloatingDirective } from "@core/directives/floating/floating.directive";
import { RouterLink } from "@angular/router";
import { extjsActionFn, resolveUrlFn } from "@core/utils/navigation.utils";
import { ExtjsActionFn, ResolveUrlFn } from "@core/types/navigation.type";
import { ApiSidebarItem, SidebarItem } from "@core/types/sidebar.type";
import { CurrentContextService } from "@core/services/current-context.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Permission } from "@shared/types/permission.types";
import { SidebarService } from "@core/services/sidebar.service";

@Component({
  selector: "app-core-sidebar-section",
  templateUrl: "./sidebar-section.component.html",
  styleUrl: "sidebar-section.component.scss",
  standalone: true,
  imports: [
    IconModule,
    MatListModule,
    UpperCasePipe,
    SidebarListItemComponent,
    MatList,
    FloatingDirective,
    RouterLink,
    NgTemplateOutlet,
    MatTooltipModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarSectionComponent {
  private readonly _sidebarService: SidebarService = inject(SidebarService);

  elementRef: ElementRef = inject(ElementRef);

  offsetRef: ElementRef = inject(SIDEBAR_OFFSET_REF);

  destroyRef: DestroyRef = inject(DestroyRef);

  get isCollapsed (): boolean {
    return this._sidebarService.collapsed();
  }

  get isCollapsedDelayed (): boolean {
    return this._sidebarService.collapsedDelayed();
  }

  @Input({ required: true })
  item!: SidebarItem;

  selectedItem: WritableSignal<SidebarItem | null> = signal(null, {
    equal: (a: ApiSidebarItem | null, b: ApiSidebarItem | null) => {
      if (a === null || b === null)
        return false;

      return a.id === b.id;
    }
  });

  selectedAccordion: WritableSignal<SidebarItem | null> = signal(null, {
    equal: (a: SidebarItem | null, b: SidebarItem | null) => {
      if (a === null || b === null)
        return false;

      return a.id === b.id;
    }
  });

  private readonly currentContextService: CurrentContextService = inject(CurrentContextService);

  constructor () {
    effect(() => {
      const items: SidebarItem[] = this.item.items ?? [ this.item ];
      const selected: SidebarItem | undefined = items.find((item: SidebarItem): boolean =>
        (this.currentContextService.currenModule() ?? "") === (item.token ?? ""));

      this.selectedItem.set(selected ?? null);
    }, { allowSignalWrites: true });
  }

  isSelected (item: SidebarItem): boolean {
    if (!this.selectedItem())
      return false;

    return this.selectedItem()?.id === item.id;
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  isFloating (item: SidebarItem): boolean {
    /* TODO: better condition */
    return Boolean(item.items?.[ 0 ] && !item.items[ 0 ]?.text && item.items[ 0 ].items?.[ 0 ]);
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  isDisabled (item: SidebarItem): boolean {
    /* TODO: role -> permission: {resource: string; role: string} */

    return Boolean(!item.status.active || item.status.missingPermissions?.length);
  }

  selectAccordion (item: SidebarItem | null): void {
    this.selectedAccordion.set(this.selectedAccordion() === item ? null : item);
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  resolveTooltip (item: SidebarItem): string | undefined {
    const missingPermissions: Permission[] | undefined = item.status.missingPermissions;
    const missingPermissionRoles: string | undefined = missingPermissions?.length ? missingPermissions.map((p: Permission) => p.role).join(", ") : undefined;
    /* Const message: string | undefined = missingPermissionRoles ? `Fehlende Berechtigung: ${ missingPermissionRoles }` : undefined; */
    const message: string | undefined = missingPermissionRoles ? `Fehlende Berechtigung` : undefined;

    return item.status.active ? message : "Deaktivierte Option";
  }

  // eslint-disable-next-line @tseslint/member-ordering
  protected readonly onExtjsAction: ExtjsActionFn = extjsActionFn();

  // eslint-disable-next-line @tseslint/member-ordering
  private readonly _resolveUrl: ResolveUrlFn = resolveUrlFn();

  protected readonly resolveUrl = (item: SidebarItem): string | undefined => (item.status.active ? this._resolveUrl(item.link) : undefined);
}
